import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../../stores'
import {
  programService,
  type PublicProgramDetailWithItems,
  type ProgramItemWithVideo,
} from '../../services'
import { Button } from '../../components/common'
import { Modal } from '../../components/common/Modal'
import { VideoPlayer } from '../../components/video/VideoPlayer'
import { useToast } from '../../components/common/Toast'
import type { Video } from '../../types/database.types'

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0:00'
  // Round to whole seconds first
  const totalSeconds = Math.round(seconds)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatTotalDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0 min'
  const totalSeconds = Math.round(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const mins = Math.floor((totalSeconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins} min`
}

function getThumbnailUrl(video: ProgramItemWithVideo['video'] | undefined): string | null {
  if (video?.thumbnail_url) return video.thumbnail_url
  if (video?.mux_playback_id) {
    return `https://image.mux.com/${video.mux_playback_id}/thumbnail.png?width=192&height=128&fit_mode=smartcrop`
  }
  return null
}

export function PublicProgramDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const toast = useToast()

  const [program, setProgram] = useState<PublicProgramDetailWithItems | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isLiking, setIsLiking] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Video preview modal state
  const [selectedExercise, setSelectedExercise] =
    useState<ProgramItemWithVideo | null>(null)
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)

  useEffect(() => {
    if (id) {
      loadProgram()
    }
  }, [id])

  const loadProgram = async () => {
    if (!id) return

    setIsLoading(true)
    try {
      const data = await programService.getPublicProgramWithItems(
        id,
        profile?.id
      )
      setProgram(data)
    } catch (error) {
      console.error('Failed to load program:', error)
      toast.error('Failed to load program')
    } finally {
      setIsLoading(false)
    }
  }

  const handleExerciseClick = (item: ProgramItemWithVideo) => {
    setSelectedExercise(item)
    setIsVideoModalOpen(true)
  }

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false)
    setSelectedExercise(null)
  }

  const handleLike = async () => {
    if (!profile?.id || !program) {
      toast.error('Please sign in to like programs')
      return
    }

    setIsLiking(true)
    try {
      const newLikedState = await programService.toggleLike(
        program.id,
        profile.id,
        program.is_liked || false
      )

      setProgram((prev) =>
        prev
          ? {
              ...prev,
              is_liked: newLikedState,
              like_count: newLikedState
                ? (prev.like_count || 0) + 1
                : Math.max((prev.like_count || 0) - 1, 0),
            }
          : null
      )
    } catch (error) {
      toast.error('Failed to update like')
    } finally {
      setIsLiking(false)
    }
  }

  const handleSave = async () => {
    if (!profile?.id || !program) {
      toast.error('Please sign in to save programs')
      return
    }

    setIsSaving(true)
    try {
      const newSavedState = await programService.toggleSave(
        program.id,
        profile.id,
        program.is_saved || false
      )

      setProgram((prev) =>
        prev ? { ...prev, is_saved: newSavedState } : null
      )

      toast.success(newSavedState ? 'Program saved' : 'Program unsaved')
    } catch (error) {
      toast.error('Failed to update save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDuplicate = async () => {
    if (!profile?.id || !program) {
      toast.error('Please sign in to use this program')
      return
    }

    if (profile.subscription_tier === 'free') {
      toast.error('Upgrade to duplicate programs')
      navigate('/dashboard/subscription')
      return
    }

    setIsDuplicating(true)
    try {
      const newProgram = await programService.duplicatePublicProgram(
        program.id,
        profile.id
      )

      if (newProgram) {
        toast.success('Program added to your library!')
        navigate(`/dashboard/programs/${newProgram.id}`)
      }
    } catch (error) {
      toast.error('Failed to duplicate program')
    } finally {
      setIsDuplicating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-4 bg-gray-200 rounded w-2/3 mb-8" />
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-full mb-2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (!program) {
    return (
      <div className="text-center py-12">
        <svg
          className="w-16 h-16 text-gray-300 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Program Not Found
        </h2>
        <p className="text-gray-500 mb-4">
          This program may have been removed or made private.
        </p>
        <Link to="/dashboard/public-programs">
          <Button>Browse Programs</Button>
        </Link>
      </div>
    )
  }

  const difficultyColors: Record<string, string> = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-orange-100 text-orange-800',
    expert: 'bg-red-100 text-red-800',
  }

  return (
    <div>
      {/* Back link */}
      <Link
        to="/dashboard/public-programs"
        className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
      >
        <svg
          className="w-4 h-4 mr-1"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Back to Browse
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Program Header */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {program.title}
                </h1>
                {program.description && (
                  <p className="mt-2 text-gray-600">{program.description}</p>
                )}
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              {program.difficulty_level && (
                <span
                  className={`px-3 py-1 text-sm rounded-full capitalize ${
                    difficultyColors[program.difficulty_level] ||
                    'bg-gray-100 text-gray-700'
                  }`}
                >
                  {program.difficulty_level}
                </span>
              )}
              {program.duration_weeks && (
                <span className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full">
                  {program.duration_weeks} weeks
                </span>
              )}
              {program.category && (
                <span className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full">
                  {program.category}
                </span>
              )}
            </div>

            {/* Stats */}
            <div className="flex items-center gap-6 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {program.item_count || 0} exercises
              </span>
              {program.total_duration && program.total_duration > 0 && (
                <span className="flex items-center gap-1">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {formatTotalDuration(program.total_duration)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {program.like_count || 0} likes
              </span>
              <span className="flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                {program.duplicate_count || 0} uses
              </span>
            </div>

            {/* Author */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                {program.author.avatar_url ? (
                  <img
                    src={program.author.avatar_url}
                    alt={program.author.full_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  program.author.full_name.charAt(0).toUpperCase()
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {program.author.full_name}
                </p>
                {program.author.specialty && (
                  <p className="text-sm text-gray-500">
                    {program.author.specialty}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Exercises List */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Exercises ({program.items?.length || 0})
            </h2>

            {program.items && program.items.length > 0 ? (
              <div className="space-y-3">
                {program.items.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => handleExerciseClick(item)}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all cursor-pointer group"
                  >
                    {/* Position Number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold group-hover:bg-blue-200">
                      {index + 1}
                    </div>

                    {/* Video Thumbnail */}
                    <div className="flex-shrink-0 w-24 h-16 bg-gray-200 rounded-lg overflow-hidden relative">
                      {getThumbnailUrl(item.video) ? (
                        <img
                          src={getThumbnailUrl(item.video)!}
                          alt={item.video?.title || 'Exercise'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      )}
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                        <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                          <svg
                            className="w-4 h-4 text-blue-600 ml-0.5"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                      {/* Duration Badge */}
                      {item.video?.duration_seconds && (
                        <div className="absolute bottom-1 right-1 bg-black/75 text-white text-xs px-1.5 py-0.5 rounded">
                          {formatDuration(item.video.duration_seconds)}
                        </div>
                      )}
                    </div>

                    {/* Exercise Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate group-hover:text-blue-600">
                        {item.video?.title || 'Untitled Exercise'}
                      </h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        {item.sets && <span>{item.sets} sets</span>}
                        {item.reps && <span>{item.reps} reps</span>}
                        {item.duration_seconds && (
                          <span>{formatDuration(item.duration_seconds)}</span>
                        )}
                        {item.rest_seconds && (
                          <span className="text-gray-400">
                            Rest: {item.rest_seconds}s
                          </span>
                        )}
                      </div>
                      {item.notes && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                          {item.notes}
                        </p>
                      )}
                    </div>

                    {/* Play indicator */}
                    <div className="flex-shrink-0 text-gray-400 group-hover:text-blue-600">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg
                  className="w-12 h-12 mx-auto mb-3 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <p>No exercises in this program yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-6">
            <Button
              onClick={handleDuplicate}
              disabled={isDuplicating}
              className="w-full mb-4"
            >
              {isDuplicating ? 'Adding to Library...' : 'Use This Program'}
            </Button>

            <div className="flex gap-2">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  program.is_liked
                    ? 'bg-red-50 border-red-200 text-red-600'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                } ${isLiking ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <svg
                  className="w-5 h-5"
                  fill={program.is_liked ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {program.is_liked ? 'Liked' : 'Like'}
              </button>

              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  program.is_saved
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <svg
                  className="w-5 h-5"
                  fill={program.is_saved ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
                {program.is_saved ? 'Saved' : 'Save'}
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-500 text-center">
              Using this program will create a copy in your library that you can
              customize.
            </p>
          </div>
        </div>
      </div>

      {/* Video Preview Modal */}
      <Modal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        title={selectedExercise?.video?.title || 'Exercise Preview'}
        size="full"
      >
        {selectedExercise && (
          <div className="space-y-6">
            {/* Video Player */}
            {selectedExercise.video?.mux_playback_id ? (
              <VideoPlayer
                video={selectedExercise.video as Video}
                autoPlay={false}
              />
            ) : (
              <div className="aspect-video bg-gray-100 rounded-xl flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-3 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="font-medium">Video not available</p>
                  <p className="text-sm mt-1">
                    {selectedExercise.video?.status === 'processing'
                      ? 'Video is still processing...'
                      : 'This video cannot be played'}
                  </p>
                </div>
              </div>
            )}

            {/* Exercise Details */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Exercise Details
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {selectedExercise.sets && (
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedExercise.sets}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">
                      Sets
                    </div>
                  </div>
                )}
                {selectedExercise.reps && (
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedExercise.reps}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">
                      Reps
                    </div>
                  </div>
                )}
                {selectedExercise.duration_seconds && (
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatDuration(selectedExercise.duration_seconds)}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">
                      Duration
                    </div>
                  </div>
                )}
                {selectedExercise.rest_seconds && (
                  <div className="bg-white rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedExercise.rest_seconds}s
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">
                      Rest
                    </div>
                  </div>
                )}
              </div>

              {selectedExercise.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Coach Notes
                  </h4>
                  <p className="text-gray-600 whitespace-pre-wrap">
                    {selectedExercise.notes}
                  </p>
                </div>
              )}

              {/* Show message if no details */}
              {!selectedExercise.sets &&
                !selectedExercise.reps &&
                !selectedExercise.duration_seconds &&
                !selectedExercise.rest_seconds &&
                !selectedExercise.notes && (
                  <p className="text-gray-500 text-center py-2">
                    No additional exercise details provided
                  </p>
                )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
